/**
 * Role / access helpers — prefer GET /api/account/access; fall back to login user fields.
 */

const ROLE_KEY = "omni_watch_role";
const ACCESS_KEY = "account_access";

/** Map legacy module keys from older roles/API payloads. */
const MODULE_KEY_ALIASES = {
  cameras: "camera_management",
  users: "user_management",
  roles: "user_management",
  ai_models: "ai_management",
  nvr: "nvr_management",
};

function resolveModuleKey(moduleKey) {
  const k = String(moduleKey ?? "").trim();
  return MODULE_KEY_ALIASES[k] ?? k;
}

function moduleCrud(access, key) {
  const canonical = resolveModuleKey(key);
  const m = access?.modules?.[canonical];
  if (!m || typeof m !== "object") return null;
  return m;
}

function moduleCrudWithLegacy(access, key) {
  const direct = moduleCrud(access, key);
  if (direct) return direct;
  if (key === "camera_management" || key === "omniwatch") {
    return moduleCrud(access, "cameras");
  }
  if (key === "user_management") {
    return moduleCrud(access, "users") ?? moduleCrud(access, "roles");
  }
  return null;
}

function hasModuleAccessMap(access) {
  const modules = access?.modules;
  return Boolean(modules && typeof modules === "object" && Object.keys(modules).length > 0);
}

/** Flashback / NVR playback — gated by the `flashback` module in role permissions. */
export function canReadFlashback(access) {
  return canReadModuleOrLegacy(access, "flashback");
}

/** Whether the signed-in user may read a dashboard module (from account access). */
export function canReadModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrudWithLegacy(access, moduleKey);
  if (m) return Boolean(m.read);
  return false;
}

/** Module read with legacy boolean flags when `modules` is not yet populated. */
export function canReadModuleOrLegacy(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  if (hasModuleAccessMap(access)) return canReadModule(access, moduleKey);
  const k = resolveModuleKey(moduleKey);
  if (k === "camera_management" || k === "omniwatch") {
    return Boolean(access.canCreateCameras);
  }
  if (k === "user_management") {
    return Boolean(access.canManageUsers || access.canManageRoles);
  }
  if (k === "zone_management" || k === "ai_management" || k === "alert_rules_management" || k === "ai_detection" || k === "alert_service" || k === "flashback" || k === "nvr_management") {
    return false;
  }
  return false;
}

/** Whether the signed-in user may create in a module. */
export function canCreateInModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrudWithLegacy(access, moduleKey);
  if (m) return Boolean(m.create);
  if (!hasModuleAccessMap(access)) {
    if (resolveModuleKey(moduleKey) === "user_management") {
      return Boolean(access.canManageUsers || access.canManageRoles);
    }
  }
  return false;
}

/** Whether the signed-in user may update in a module. */
export function canUpdateInModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrudWithLegacy(access, moduleKey);
  if (m) return Boolean(m.update);
  if (!hasModuleAccessMap(access)) {
    if (resolveModuleKey(moduleKey) === "user_management") {
      return Boolean(access.canManageUsers || access.canManageRoles);
    }
  }
  return false;
}

/** Whether the signed-in user may delete in a module. */
export function canDeleteInModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrudWithLegacy(access, moduleKey);
  if (m) return Boolean(m.delete);
  if (!hasModuleAccessMap(access)) {
    if (resolveModuleKey(moduleKey) === "user_management") {
      return Boolean(access.canManageUsers || access.canManageRoles);
    }
  }
  return false;
}

/** CRUD flags for a module (from GET /api/account/access). */
export function getModuleCrud(access, moduleKey) {
  return {
    read: canReadModuleOrLegacy(access, moduleKey),
    create:
      resolveModuleKey(moduleKey) === "camera_management"
        ? canCreateCameras(access)
        : canCreateInModule(access, moduleKey),
    update: canUpdateInModule(access, moduleKey),
    delete: canDeleteInModule(access, moduleKey),
  };
}

function administrationSectionsMap(access) {
  const s = access?.administrationSections;
  return s && typeof s === "object" ? s : null;
}

/** Single Administration tab/section from GET /api/account/access `administrationSections`. */
export function canAccessAdministrationSection(access, sectionKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const sections = administrationSectionsMap(access);
  if (sections) return Boolean(sections[sectionKey]);
  return false;
}

/** sectionKey → legacy modules.* read key when administrationSections is absent. */
const ADMIN_SECTION_LEGACY_MODULE = {
  cameraManagement: "camera_management",
  zoneManagement: "zone_management",
  nvrManagement: "nvr_management",
  aiManagement: "ai_management",
  recycleBinManagement: "recycle_bin_management",
  userManagement: "user_management",
};

/**
 * Route/tab gate for one Administration section.
 * Prefer administrationSections; fall back to module read flags when the map is missing.
 */
export function canAccessAdminSectionOrLegacy(access, sectionKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;

  const sections = administrationSectionsMap(access);
  if (sections && Object.keys(sections).length > 0) {
    if (sections[sectionKey] !== undefined) {
      return Boolean(sections[sectionKey]);
    }
  }

  const moduleKey = ADMIN_SECTION_LEGACY_MODULE[sectionKey];
  if (moduleKey) return canReadModuleOrLegacy(access, moduleKey);
  return false;
}

/**
 * Ordered admin child paths (same order as Administration tabs).
 * Used for index redirect and deep-link fallbacks.
 */
export const ADMINISTRATION_SECTION_ROUTES = [
  { path: "camera-mgmt", sectionKey: "cameraManagement" },
  { path: "zone-mgmt", sectionKey: "zoneManagement" },
  { path: "ai-models-mgmt", sectionKey: "aiManagement" },
  { path: "recycle-bin-mgmt", sectionKey: "recycleBinManagement" },
  { path: "nvr-mgmt", sectionKey: "nvrManagement" },
  { path: "user-mgmt", sectionKey: "userManagement" },
];

/** First Administration child path the user may open, or null. */
export function getFirstAllowedAdministrationPath(access) {
  const hit = ADMINISTRATION_SECTION_ROUTES.find((r) =>
    canAccessAdminSectionOrLegacy(access, r.sectionKey),
  );
  return hit?.path ?? null;
}

/** Show Administration menu / routes — any section true, or legacy module flags. */
export function canAccessAdministration(access) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;

  const sections = administrationSectionsMap(access);
  if (sections) {
    return Object.values(sections).some(Boolean);
  }

  if (access.canManageUsers || access.canManageRoles) return true;
  return (
    canReadModule(access, "camera_management") ||
    canReadModule(access, "nvr_management") ||
    canReadModule(access, "zone_management") ||
    canReadModule(access, "ai_management") ||
    canReadModule(access, "alert_rules_management") ||
    canReadModule(access, "recycle_bin_management") ||
    canReadModule(access, "user_management")
  );
}

/** Admin / super-admin users do not need per-camera assignment. */
export function isPrivilegedSystemRole(systemRoleLabel) {
  const s = String(systemRoleLabel ?? "")
    .toLowerCase()
    .trim();
  return s === "admin" || s === "super admin" || s === "super_admin" || s === "superadmin";
}

/** Built-in Super Admin account only (not Admin). */
export function isSuperAdminSystemRole(systemRoleLabel) {
  const s = String(systemRoleLabel ?? "")
    .toLowerCase()
    .trim()
    .replace(/-/g, "_")
    .replace(/\s+/g, " ");
  return s === "super admin" || s === "super_admin" || s === "superadmin";
}

/**
 * Built-in Super Admin / Admin user accounts — cannot be deleted (backend + UI).
 * @param {{ systemRoleLabel?: string | null, role?: string } | null | undefined} user
 */
export function isProtectedAdminUser(user) {
  if (!user || typeof user !== "object") return false;
  if (isPrivilegedSystemRole(user.systemRoleLabel)) return true;
  const role = String(user.role ?? "")
    .toLowerCase()
    .trim();
  return role === "admin" || role === "super_admin" || role === "superadmin";
}

/**
 * Only Super Admin and Admin are locked built-in roles.
 * Regular "User" accounts are created by Super Admin or Admin (not a locked role).
 */
export function isBuiltInRole(role) {
  if (!role || typeof role !== "object") return false;
  if (isPrivilegedSystemRole(role.slug) || isPrivilegedSystemRole(role.systemRole)) {
    return true;
  }
  const name = String(role.role ?? role.name ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
  if (name === "super admin" || name === "admin" || name === "superadmin") {
    return true;
  }
  // Predefined "User" is not locked — only SA/Admin are.
  if (role.isPredefined === true) {
    return isPrivilegedSystemRole(role.slug) || isPrivilegedSystemRole(name);
  }
  return false;
}

/** Predefined panel admin role in user form (not custom roles named "Admin"). */
export function isPrivilegedRoleOption(roleId, roleOptions) {
  if (!roleId || !Array.isArray(roleOptions)) return false;
  const opt = roleOptions.find((o) => o.id === roleId);
  if (!opt) return false;
  if (opt.isPredefined === false) return false;
  if (opt.systemRole) return isPrivilegedSystemRole(opt.systemRole);
  if (opt.slug) return isPrivilegedSystemRole(opt.slug);
  return false;
}

export function canCreateCameras(access) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  if (canCreateInModule(access, "camera_management")) return true;
  return Boolean(access.canCreateCameras);
}

export function isSystemAdmin(access) {
  return Boolean(access?.isAdmin);
}

/** Built-in Super Admin only (not Admin). From GET /api/account/access. */
export function isSuperAdminAccess(access) {
  if (!access || typeof access !== "object") return false;
  if (access.isSuperAdmin === true) return true;
  return isSuperAdminSystemRole(access.systemRole);
}

/** @deprecated Prefer account access; kept for legacy user object checks. */
export function isAdminUser(user, access) {
  if (access && typeof access === "object") return Boolean(access.isAdmin);
  if (!user || typeof user !== "object") return false;
  const r = String(user.role ?? user.user_type ?? user.userType ?? "")
    .toLowerCase()
    .trim();
  if (r === "admin") return true;
  if (user.is_admin === true || user.isAdmin === true) return true;
  return false;
}

export function getAppRoleFromAccess(access) {
  if (access?.isAdmin) return "admin";
  return "user";
}

export function getAppRoleFromUser(user, access) {
  if (access) return getAppRoleFromAccess(access);
  return isAdminUser(user) ? "admin" : "user";
}

export function persistAccountAccess(access) {
  try {
    if (access) {
      sessionStorage.setItem(ACCESS_KEY, JSON.stringify(access));
    } else {
      sessionStorage.removeItem(ACCESS_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function loadAccountAccess() {
  try {
    const raw = sessionStorage.getItem(ACCESS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function syncAppRoleFromAccess(access) {
  try {
    sessionStorage.setItem(ROLE_KEY, getAppRoleFromAccess(access));
  } catch {
    /* ignore */
  }
}

export function syncAppRoleFromUser(user, access) {
  try {
    sessionStorage.setItem(ROLE_KEY, getAppRoleFromUser(user, access));
  } catch {
    /* ignore */
  }
}

export function clearAppRole() {
  try {
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(ACCESS_KEY);
  } catch {
    /* ignore */
  }
}
